/**
 * Instalación de las reacciones del cliente de API, y lo que cada una muestra.
 *
 * El cliente decide **cuándo** reacciona; este módulo decide **qué se ve**. La separación no es
 * ceremonia: navegar necesita el enrutador, avisar necesita estado de React y abrir el flujo de
 * consentimiento necesita montar un diálogo, y ninguna de las tres cosas puede vivir en un
 * módulo que también se usa desde una prueba de Node sin DOM.
 *
 * **El `428` es el caso interesante.** El diseño pide abrir el flujo de consentimiento y
 * *volver a la acción original* al aceptarlo. Se resuelve con una promesa que el cliente de API
 * espera: la reacción monta el diálogo y devuelve una promesa que no se resuelve hasta que la
 * persona acepta —y entonces vale `true`, y el cliente repite la petición— o cierra el diálogo,
 * y entonces vale `false` y el error sigue su camino. Desde la vista que hizo la llamada no se
 * ve nada de esto: su `await` simplemente tarda más y termina con los datos.
 *
 * La alternativa era navegar a la ruta de consentimientos con la ruta de vuelta en la cadena de
 * consulta. Se descarta porque **pierde la acción**: si el `428` interrumpió el guardado de un
 * formulario, navegar afuera se lleva lo escrito, y volver deja a la persona frente al
 * formulario vacío teniendo que recordar qué había puesto. Con el diálogo la acción está
 * suspendida, no cancelada.
 *
 * **El `401` navega en lugar de dibujar.** Es la única reacción que cambia de ruta, porque es la
 * única en que la vista actual dejó de ser válida: no hay sesión con la que dibujarla.
 */

import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { instalarReaccionesDeApi, type ErrorDeApi } from './apiClient';
import { ingresoConDestino } from './rutas';
import { useSesion } from './sesion';

/**
 * La pieza de aceptación se carga de forma diferida, y no por gusto de simetría con las rutas.
 *
 * Este módulo cuelga del armazón, así que **está en el paquete inicial**. Una importación estática
 * de la aceptación arrastraría con ella los esquemas compartidos y su biblioteca de validación al
 * arranque de toda la aplicación, para una pantalla que la mayoría de las sesiones no ve nunca:
 * el `428` solo ocurre cuando se publicó un texto nuevo y todavía no se aceptó. Con `lazy` el
 * fragmento viaja cuando el `428` llega, que es exactamente cuando hace falta.
 */
const AceptacionDeTratamiento = lazy(async () => ({
  default: (await import('../componentes/aceptacionDeTratamiento')).AceptacionDeTratamiento,
}));

/** Avisos globales que el armazón dibuja como banda sobre el contenido. */
export interface AvisosDeApi {
  /** El Worker ya no soporta esta versión del paquete: hay que recargar. */
  readonly clienteObsoleto: boolean;

  /** La última petición no llegó a destino (criterio 26.8). */
  readonly sinConexion: boolean;
}

const CONTEXTO = createContext<AvisosDeApi>({ clienteObsoleto: false, sinConexion: false });

/** Avisos globales vigentes. Fuera del proveedor no hay ninguno, que es un valor correcto. */
export function useAvisosDeApi(): AvisosDeApi {
  return useContext(CONTEXTO);
}

/**
 * Monta las reacciones y el diálogo de consentimiento.
 *
 * Va dentro del enrutador y dentro del proveedor de sesión, porque usa los dos.
 *
 * @param props.children árbol de la aplicación
 */
export function ProveedorDeReacciones({ children }: { readonly children: ReactNode }) {
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const { marcarPerdida } = useSesion();

  const [clienteObsoleto, setClienteObsoleto] = useState(false);
  const [sinConexion, setSinConexion] = useState(false);

  /** Versión pendiente y resolvedor de la espera, mientras el diálogo del `428` está abierto. */
  const [puerta, setPuerta] = useState<{
    readonly version: string | null;
    readonly resolver: (acepto: boolean) => void;
  } | null>(null);

  /**
   * Ubicación actual en una referencia, y no en el cuerpo de las reacciones.
   *
   * Las reacciones se instalan una vez y viven en un módulo, así que si capturaran `ubicacion`
   * conservarían la de la instalación: el destino que se guardaría al perder la sesión sería la
   * ruta en la que la aplicación arrancó y no la que la persona estaba usando. La referencia se
   * actualiza en cada dibujado y la reacción la lee al dispararse.
   */
  const ubicacionActual = useRef(ubicacion);
  ubicacionActual.current = ubicacion;

  const alPerderLaSesion = useCallback(() => {
    marcarPerdida();

    const { pathname, search } = ubicacionActual.current;
    // `replace` y no un empujón al historial: la ruta que se abandonó ya no se puede dibujar
    // sin sesión, así que dejarla en el historial solo serviría para que el botón de volver
    // produzca otro `401`.
    void navegar(ingresoConDestino(`${pathname}${search}`), { replace: true });
  }, [marcarPerdida, navegar]);

  const alFaltarConsentimiento = useCallback(
    (error: ErrorDeApi) =>
      new Promise<boolean>((resolver) => {
        setPuerta({ version: error.versionDeConsentimientoPendiente, resolver });
      }),
    [],
  );

  useEffect(
    () =>
      instalarReaccionesDeApi({
        alPerderLaSesion,
        alFaltarConsentimiento,
        alQuedarObsoletoElCliente: () => {
          setClienteObsoleto(true);
        },
        alFallarLaRed: () => {
          setSinConexion(true);
        },
        alRecuperarLaRed: () => {
          setSinConexion(false);
        },
      }),
    [alPerderLaSesion, alFaltarConsentimiento],
  );

  const cerrarPuerta = useCallback(
    (acepto: boolean) => {
      puerta?.resolver(acepto);
      setPuerta(null);
    },
    [puerta],
  );

  const avisos = useMemo<AvisosDeApi>(
    () => ({ clienteObsoleto, sinConexion }),
    [clienteObsoleto, sinConexion],
  );

  return (
    <CONTEXTO.Provider value={avisos}>
      {children}
      {puerta !== null && <DialogoDeConsentimiento alCerrar={cerrarPuerta} />}
    </CONTEXTO.Provider>
  );
}

/**
 * Diálogo del consentimiento pendiente.
 *
 * Es un `<dialog>` nativo abierto como modal, que es lo que da el atrapado de foco, el cierre
 * con la tecla de escape y el fondo inerte sin escribir ninguna de las tres cosas a mano. Se
 * abre desde un efecto porque `showModal` es imperativo: el atributo `open` del marcado abre el
 * diálogo pero **no** como modal, y entonces el foco se escapa al contenido de atrás.
 *
 * Cerrar por escape cuenta como abandonar, no como aceptar, y por eso el `close` resuelve
 * `false`. Es la elección segura: registrar una aceptación porque alguien pulsó una tecla para
 * salir sería registrar un consentimiento que nadie dio.
 *
 * @param props.alCerrar resolvedor de la espera del cliente de API
 */
function DialogoDeConsentimiento({
  alCerrar,
}: {
  readonly alCerrar: (acepto: boolean) => void;
}) {
  const referencia = useRef<HTMLDialogElement>(null);
  const acepto = useRef(false);

  useEffect(() => {
    referencia.current?.showModal();
  }, []);

  return (
    <dialog
      aria-labelledby="titulo-de-la-puerta"
      className="aisalud-dialogo"
      onClose={() => {
        alCerrar(acepto.current);
      }}
      ref={referencia}
    >
      <h2 id="titulo-de-la-puerta">Falta aceptar el consentimiento</h2>
      <p>
        Para completar lo que estabas haciendo necesitas aceptar el consentimiento de tratamiento
        de datos vigente. Al aceptarlo continuamos con la acción que quedó pendiente.
      </p>

      <Suspense fallback={<p role="status">Cargando el consentimiento…</p>}>
        <AceptacionDeTratamiento
          alAceptar={() => {
            acepto.current = true;
            referencia.current?.close();
          }}
        />
      </Suspense>

      <button
        className="aisalud-boton"
        onClick={() => {
          referencia.current?.close();
        }}
        type="button"
      >
        Ahora no
      </button>
    </dialog>
  );
}
